import React,{useState, useEffect, memo} from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs, ProgressBar, Icon,H4, Button, Dialog, Classes, Intent, FormGroup, InputGroup, Toaster } from '@blueprintjs/core';
import { Paper } from '@material-ui/core';
import { IconNames } from '@blueprintjs/icons';
import Cookies from 'js-cookie';
import EnhancedSingleSelect from '../../common/EnhancedSingleSelect';


import Cooccurrences from './cooc/Cooccurrences';
import Concordances from './concord/Concordances';
import Recap from './recap/Recap';
import Statistics from './stats/Statistics';
import WordSketch from './wordSketch/WordSketch';
import ClusterContexts from './clusterContexts/ClusterContexts';
import Multivar from './multivar/Multivar';
import ALR from './alr/ALR';
import MLM from './mlm/MLM';
import Map from './map/map';
import Translation from './translation/Translation';
import Chronogram from './chronogram/Chronogram';
import { FRONT_USER_ROLE_COOKIE_NAME } from '../../auth/constants.js';
import { lemmaCat }  from '../../utils.js'
import Realizations from './real/Realizations';
import SpecialProgressBar from './SpecialProgressBar';



export default memo(function Results({collections,results,performSearch,pasteQuery,runMLM,currentResultIndex,onSelectResult,queryHistory,completeSearch}) {
  const { t }=useTranslation();

  // state initialisation
  const [selectedTabId,setSelectedTabId]=useState(results.mlmStats?'mlm':'stats');
  const [waitingForMultivar,setWaitingForMultivar]=useState(false);
  const [multivarIsReady,setMultivarIsReady]=useState(false);
  const [waitingForAlr,setWaitingForAlr]=useState(false);
  const [retryToLoadAlr,setRetryToLoadAlr]=useState(false);
  const [alrData,setAlrData]=useState({});
  const [isOpenModal,setIsOpenModal]=useState(false);
  const [resultsAreSaved,setResultsAreSaved]=useState(false);
  const [resultsName,setResultsName]=useState(lemmaCat(queryHistory[currentResultIndex]).replace(/\d+\s:\s|_\.\*/g,''));
  const [keywords,setKeywords]=useState("");

  // componentDidMount
  useEffect(()=>{
    document.title =  'query='+ lemmaCat(results.query);
  });


  const computeMultivar = () => {
    const sessionId = results['session_name'];
    const endpoint = '/compute_multivar.ajax.php';
  
    setWaitingForMultivar(true);
    fetch(
      process.env.REACT_APP_API_BASE + endpoint + '?session_id=' + sessionId, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      }
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${endpoint}`);
        return;
      }
      res.json().then((data) => {
        setMultivarIsReady(true);
        handleTabChange('multivar');
      });
    })
  };

  // this function try to directly load the json result without (usefull after a timeout)
  const doRetryToLoadALR = () => {
    const sessionId = results['session_name'];
    const endpoint = '/sessions/'+sessionId+'/output.alr.json';
   
    //setWaitingForAlr(true);
    fetch(
      process.env.REACT_APP_API_BASE + endpoint , {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      },
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${endpoint}`);
        //setWaitingForAlr(false);
        setRetryToLoadAlr(true);
        return;
      }
      res.json().then((data) => {
        setAlrData(data);
        setRetryToLoadAlr(false);
        handleTabChange('alr');
      });
    }).catch(()=>{
      console.log(`Issue reaching ${endpoint} (timeout ?)`);
      setWaitingForAlr(false);
      setRetryToLoadAlr(true);
      return;
    })
  };

  const computeALR = () => {
    const sessionId = results['session_name'];
    const endpoint = '/search.ajax.php';


    const data={
      session_id:sessionId,
      runALR:true,
      completeSearch
    };
    setWaitingForAlr(true);
    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(data),
      },
      //600000,"TimeOut has been reached for ALR request"
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${endpoint}`);
        setRetryToLoadAlr(true);
        return;
      }
      res.json().then((data) => {
        setRetryToLoadAlr(false);
        handleTabChange('alr');
      });
    }).catch(()=>{
      console.log(`Issue reaching ${endpoint} (timeout ?)`);
      setWaitingForAlr(false); 
      setRetryToLoadAlr(true);
      return;
    })
  };
  
  const computeALRComplete = () => {
    const sessionId = results['session_name'];
    const endpoint = '/search.ajax.php';
    setWaitingForAlr(true);
    const data={
      session_id:sessionId,
      runALR:true,
      completeSearch:true
    };

    fetch(
      process.env.REACT_APP_API_BASE + endpoint , {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    .then((res) => {
      if (res.status !== 200) {
        console.log(`Issue reaching ${endpoint}`);
        return;
      }
      res.json().then((data) => {
        setWaitingForAlr(false);
        handleTabChange('alr');
      });
    })
  };

  const onAlrReady=(alrData) => {
    setWaitingForAlr(false); 
    setAlrData(alrData);
  };


  const handleTabChange = (navbarTabId) => {
    setSelectedTabId(navbarTabId);
    //  this trick is to cope with the react-apex-chart show problem
    window.dispatchEvent(new Event('resize'));
  }

  const saveResults= ()=> {
    toggleModal();
    const endpoint = '/save_user_data.ajax.php';

    const data = {
      type:'results',
      name:resultsName,
      keywords:keywords,
      content:results,
    };


    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(data),
      })
      .then((res) => {
        if (res.status !== 200) {
          console.log(`Issue reaching ${endpoint}`);
          return;
        }
        res.json().then((data) => {
          const toaster= Toaster.create({});
          toaster.show({
            message: t('resultsHaveBeenSaved'),
            intent: 'success',
            icon: 'tick',
          });
          setResultsAreSaved(true);
        })
      })
      .catch(() => {
        console.log(`Network error when trying to fetch ${process.env.REACT_APP_API_BASE + endpoint}`);
      })

  }

  const  toggleModal = () => {
    setIsOpenModal( !isOpenModal );
  }

  // previously render()
  const coocData=results.cooc;
  const statsData=results.stats;
  const coocSpecData=results.coocSpec;
  const concordsData=results.concords;
  const recapData=results.recap;
  const wordSketchData=results.wordsketch;
  const wordSketchByRealData=results.wordsketchByReal;
  const chronoData=results.chronoData;
  const MLMData=results.mlmStats;
  const transData=results.alignedSelectedLems;
  // when the query is empty and all sentences are retrieved
  const retrieveAllSents=results.retrieveAllSents;
  const mapData=results.map;
  const estimatedResults={};
  const subCorpora=Object.keys(statsData.byCorpus).sort();
  const missingSubCorpora=results.corpusList.filter( name => subCorpora.indexOf(name) === -1);

  const concordSampling=!! (results.concordSamplingWithSimpleQueries);
  const subCorpusDisplayByFile=(results.params.subCorpusDisplay==='byFile');

  // preparing the display of the estimated result message
  if (! results.concordSamplingWithSimpleQueries) {
     if (parseInt(statsData.nbParsedSents)!==parseInt(statsData.nbSents2Parse)) {
      estimatedResults['_global_']=true;
    } 
    for (var subCorpus in statsData.byCorpus) {
      if (parseInt(statsData.byCorpus[subCorpus].nbParsedSents)!==parseInt(statsData.byCorpus[subCorpus].nbSents2Parse)) {
        estimatedResults[subCorpus]=true;
      } 
    }
  }
  //const urlParams = new URLSearchParams(window.location.search);
  const sessionId = results['session_name'];  // l'id session n'est pas toujours fourni en get - du coup on le prend dans les résultats
  const coocs=coocData['_global_'] ? coocData['_global_'].length>0: false;
  const ws=Object.keys(wordSketchData).length>0;
  const clusterData=Object.keys(results.contextTrees).length>0;
  const csvDelimiter=results.params.csvDelimiter;
  const metadataFormat=results.params.metadataFormat;

  const cooc = coocs ? <Cooccurrences
                            subCorpora={subCorpora} 
                            sessionId={sessionId} 
                            cooc={coocData} 
                            coocSpec={coocSpecData} 
                            estimatedResults={estimatedResults} 
                            collocQuery={results.collocQuery}
                            performSearch={performSearch}
                            concordSampling={concordSampling}
                            csvDelimiter={csvDelimiter}
                            dispCriterion={results.params.coocDispCriterion ? results.params.coocDispCriterion : results.params.dispCriterion}
                />:"";
  const conc = concordsData ? <Concordances 
                            subCorpora={subCorpora} 
                            collections={collections}
                            concordSampling={concordSampling}
                            concordsByCorpus={concordsData.concordsByCorpus} 
                            docMeta={concordsData.docMeta} 
                            lang={concordsData.lang}
                            aligned_language={concordsData.aligned_language}
                            concordSpan={concordsData.concordSpan}
                            attr_val_doc={concordsData.attr_val_doc}
//                            {...concordsData} 
                            performSearch={performSearch}
                            pasteQuery={pasteQuery}
                            queryNumber={results.queryNumber}
                            retrieveAllSents={retrieveAllSents}
                            csvDelimiter={csvDelimiter}
                            metadataFormat={metadataFormat}

                />:"";
  const recap = recapData ? <Recap recap={recapData} />:"";
  const stats = <Statistics 
                            subCorpora={subCorpora}
                            missingSubCorpora={missingSubCorpora}
                            stats={statsData} 
                            query={results.query} 
                            querySvg={results.querySvg} 
                            estimatedResults={estimatedResults}
                            pasteQuery={pasteQuery}
                            performSearch={performSearch}
                            runMLM={runMLM}
                            sessionId={sessionId}
                            dispCriterion={results.params.dispCriterion}
                            subCorpusDisplayByFile={subCorpusDisplayByFile}
                            csvDelimiter={csvDelimiter}

                />;
  const reals = Object.keys(results.stats.realizations).length > 1 ? 
                    <Realizations
                            subCorpora={subCorpora} 
                            stats={statsData} 
                            estimatedResults={estimatedResults}
                            csvDelimiter={csvDelimiter}

                    />:"";
  const wordSketch= ws ? <WordSketch 
                            subCorpora={subCorpora} 
                            sessionId={sessionId} 
                            query={results.query} 
                            ws={wordSketchData} 
                            wsByReal={wordSketchByRealData}
                            estimatedResults={estimatedResults}
                            performSearch={performSearch}
                            concordSampling={concordSampling}
                            csvDelimiter={csvDelimiter}

                    />:"";
  const clusterContexts= clusterData ? <ClusterContexts results={results} /> : "";

  const chronogram = chronoData ? 
                    <Chronogram 
                            data={chronoData} 
                            collocQuery={results.collocQuery} 
                            sessionId={sessionId} 
                            stats={statsData} 
                            warningIncompleteExtraction={estimatedResults['_global_']}
                            performSearch={performSearch}
                            csvDelimiter={csvDelimiter}

                    />:"";
  const mlm = MLMData ? <MLM mlmStats={MLMData} query={results.query} csvDelimiter={csvDelimiter} />:"";
  
  const map = mapData ? <Map 
                            mapData={mapData}
                            subCorpora={subCorpora} 
                            docMeta={concordsData.docMeta} 
                            estimatedResults={estimatedResults['_global_']}
                            concordsByCorpus={concordsData.concordsByCorpus} 
                            csvDelimiter={csvDelimiter}

                         />:"";

  const translation = transData ? <Translation data={transData} />:"";

  // alr display in two steps : first the button, then the results
  // but for incompleteExtraction, the button remains in order to runAgain
  var alr;
  if (waitingForAlr) {
    alr= <SpecialProgressBar session_id={sessionId} addResults={onAlrReady} alr={true}/>
  } else if (alrData['occ']) {
    //console.log("alrData",alrData);
    alr =  
      <>
          <br/> 
          <center><button onClick={computeALR}>{t('runAlrAgainWithNewParam')}</button></center>
          <br/> 

          <ALR 
            displayRunAgain={alrData.status==="incomplete"} 
            runAgain={computeALRComplete} 
            alrOcc={alrData['occ']} 
            alrSpec={alrData['spec']}
            performSearch={performSearch}
            csvDelimiter={csvDelimiter}

          />
      </>
  } else if (retryToLoadAlr) {
    alr=
      <>
        <div>
          <Icon icon={IconNames.INFO_SIGN} iconSize={20} style={{display:"inline"}}/>
          <span dangerouslySetInnerHTML={{__html:t('retryToLoadAlr')}} ></span>
        </div>
        <br/>
        <center><button onClick={doRetryToLoadALR}>{t('reload')}</button></center>
      </>
  } else {
    alr=<>
          <div>
            <Icon icon={IconNames.INFO_SIGN} iconSize={20} style={{display:"inline"}}/>
            <span dangerouslySetInnerHTML={{__html:t('alrVersion')}} ></span>
          </div>
          <br/>
          <center><button onClick={computeALR}>{t('runAlr')}</button></center>
        </>
  } 

  // multivar display in two steps : first the button, then the results
  const runMultivar = <>
    { ! waitingForMultivar &&
      <center><button onClick={computeMultivar}>{t('runMultivar')}</button></center>
    }
    { waitingForMultivar && <ProgressBar/> }
  </>

  const multivar= <Multivar sessionId={sessionId} />;
  
  // ALR tab is displayed only if logged user role is "user+"
  const userPlus = (Cookies.get(FRONT_USER_ROLE_COOKIE_NAME)==='user+') ? true: false ;
  
  const navSelect=  
      <div style={{display:'flex'}}>
        <EnhancedSingleSelect
            label={t('query')}
            name='query'  // name is also used to make the input's `id`
            options={queryHistory}
            value={queryHistory[currentResultIndex]}
            onChange={onSelectResult}
        />
        &nbsp;<Button onClick={()=> { pasteQuery(results.query)} } style={{height:"30px"}} icon="edit" small="true" />
        &nbsp;
        { 
          ! resultsAreSaved && 
          <Button onClick={toggleModal} style={{height:"30px"}} icon="floppy-disk" small="true" />
        }
        { 
          resultsAreSaved && 
          <Button disabled={true} style={{height:"30px"}} icon="saved" small="true" />
        }

      </div>;

  if (statsData.freq!==null && parseInt(statsData.freq)!==0) {

    //console.log("render",results.query);
    return (
      <>
        {navSelect}
        <Paper elevation={3} style={{padding:10}}>
          {/* <div>{t('query')} <span style={{color:'green'}}>{results.query}</span></div> */}
          <Tabs onChange={handleTabChange} selectedTabId={selectedTabId}>
            <Tab id='stats' title={t('stats')} panel={stats} />
            { reals &&
              <Tab id='reals' title={t('realizations')} panel={reals} />
            }
            { conc && 
              <Tab id='conc' title={t('conc')} panel={conc} />
             }
            { coocs && 
              <Tab id='cooc' title={t('cooc')} panel={cooc} />
            }
            { ws &&
              <Tab id='ws' title={t('ws')} panel={wordSketch} />
            }
            { userPlus  && (!retrieveAllSents) && clusterData && 
              <Tab id='clust' title={t('clust')} panel={clusterContexts} />
            }
            { userPlus  && (!retrieveAllSents) && 
              <Tab id='alr' title={t('alr')} panel={alr} />
            } 
            {
              chronoData &&
              <Tab id='chrono' title={t('chronogram')} panel={chronogram} />
            }
            { userPlus && (! multivarIsReady) && (!retrieveAllSents) &&
              <Tab id='runMultivar' title={t('multivarStats')} panel={runMultivar} />
            } 
            { userPlus && multivarIsReady && (!retrieveAllSents) &&
              <Tab id='multivar' title={t('multivarStats')} panel={multivar} />
            }
            {
              MLMData && 
              <Tab id='mlm' title={t('mlmStats')} panel={mlm} />
            }
            {
              mapData && 
              <Tab id='map' title={t('textMap')} panel={map} />
            }
            {
              transData && 
              <Tab id='translation' title={t('translation')} panel={translation} />
            }
            <Tabs.Expander />
            { recap && <Tab id='recap' title={t('recap')} panel={recap} /> }
          </Tabs>
        </Paper>
        <Dialog
            isOpen={isOpenModal}
            onClose={toggleModal}
            title={t('saveResults')}
            style={{'width': '400px'}}  // 400px seems a good deal
          >
            <div className={Classes.DIALOG_BODY}>
              {t('currentResultsCanBeSavedInYourPersonalDirectoriesOnTheServer')}
              <br/>
              <FormGroup label={t('name')} labelFor="resultsName" >
                  <InputGroup
                    id="resultsName"
                    value={resultsName}
                    onChange={(e) => setResultsName(e.target.value)}
                  />
                </FormGroup>
                <FormGroup label={t('keywords')} labelFor="keywords">
                  <InputGroup
                    id="keywords"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                  />
                </FormGroup>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                

                <Button onClick={toggleModal}>{t('close')}</Button>

                <Button
                    onClick={saveResults}
                    intent={Intent.PRIMARY}
                >
                    {t('confirmSaving')}
                </Button>
              </div>
            </div>
        </Dialog>
      </>
    );
  } else {
    return (
      <>
        {navSelect}
        <Paper elevation={3} style={{padding:10}}>
          <br/>
          <br/>
          <center>
            <H4>{t("noResultsFor")}</H4>
            <br/>
            <br/>
            <div style={{color:'#106ba3'}}>{results.query}</div>
            <br/>
            {(results.status==="incomplete") && 
              <div>
                <p className="warn">{t('onlyASampleOfTheCorpusHasBeenSearched')}</p>
                <Button onClick={ () => { performSearch({useCompleteSearch:true}) }}>{t('runAgain')}</Button>
              </div>
            }
          </center>
        </Paper>
      </>
    );
  }
},areEqual);

function areEqual(prevProps, nextProps) {
  if (prevProps.results===nextProps.results) {
    //console.log("no change");
    return true;
  } else {
    return false;
  }
}